import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Globe, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtNum = (n) => Math.round(Number(n) || 0).toLocaleString();

function Mode2Row({ t }) {
  const cur = t.currency || '';
  const fixed = fmtNum(t.fixedForeign);
  const recv = fmtNum(t.receivedForeign);
  const outs = fmtNum(t.outstandingForeign);
  const rate = t.effectiveAvgRate ? `₹${t.effectiveAvgRate}` : '—';
  return (
    <TableRow key={t.tripId} data-testid={`fx-mode2-row-${t.displayTripId}`}>
      <TableCell>
        <div className="font-mono text-xs">{t.displayTripId}</div>
        <div className="text-xs text-slate-500">{t.guestName} · {t.destination}</div>
      </TableCell>
      <TableCell className="text-right font-mono">{cur} {fixed}</TableCell>
      <TableCell className="text-right font-mono text-emerald-700">{cur} {recv}</TableCell>
      <TableCell className="text-right font-mono text-rose-700">{cur} {outs}</TableCell>
      <TableCell className="text-right font-mono">{inr(t.collectedInr)}</TableCell>
      <TableCell className="text-right font-mono font-semibold">{rate}</TableCell>
    </TableRow>
  );
}

function Mode1Row({ t }) {
  const impact = Number(t.totalInrImpact) || 0;
  const impactClass = impact > 0 ? 'text-rose-700' : (impact < 0 ? 'text-emerald-700' : 'text-slate-500');
  const arrow = impact > 0
    ? <TrendingUp className="h-3.5 w-3.5" />
    : (impact < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : null);
  const hasActual = t.hasActualRates;
  return (
    <TableRow key={t.tripId} data-testid={`fx-mode1-row-${t.displayTripId}`}>
      <TableCell>
        <div className="font-mono text-xs">{t.displayTripId}</div>
        <div className="text-xs text-slate-500">{t.guestName} · {t.destination}</div>
      </TableCell>
      <TableCell className="text-center">{t.foreignItems}</TableCell>
      <TableCell className="text-center">{hasActual ? 'Yes' : <span className="text-slate-400">pending</span>}</TableCell>
      <TableCell className={`text-right font-mono font-semibold ${impactClass}`}>
        <span className="inline-flex items-center gap-1 justify-end">
          {arrow}
          {hasActual ? inr(impact) : '—'}
        </span>
      </TableCell>
    </TableRow>
  );
}

export default function FxReport() {
  const [tab, setTab] = useState('mode2');
  const [mode1, setMode1] = useState(null);
  const [mode2, setMode2] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m1, m2] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/fx/report/mode1`, { withCredentials: true }).then((r) => r.data).catch(() => ({ trips: [] })),
        axios.get(`${BACKEND_URL}/api/fx/report/mode2`, { withCredentials: true }).then((r) => r.data).catch(() => ({ trips: [] })),
      ]);
      setMode1(m1); setMode2(m2);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const m2Trips = (mode2 && mode2.trips) || [];
  const m1Trips = (mode1 && mode1.trips) || [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="fx-report-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-indigo-600" />
            <h1 className="text-2xl font-bold text-slate-900">FX Reporting</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">Two distinct views — a fixed-INR quote's rate variance vs a fixed-foreign quote's INR collection.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="fx-report-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex gap-2">
        <button
          type="button" data-testid="fx-tab-mode2"
          onClick={() => setTab('mode2')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'mode2' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          Mode 2 — Foreign quotes (INR collection)
        </button>
        <button
          type="button" data-testid="fx-tab-mode1"
          onClick={() => setTab('mode1')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'mode1' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          Mode 1 — INR quotes (rate variance)
        </button>
      </div>

      {tab === 'mode2' ? (
        <Card className="overflow-hidden" data-testid="fx-mode2-view">
          {m2Trips.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No foreign-currency quotes yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trip</TableHead>
                  <TableHead className="text-right">Fixed (foreign)</TableHead>
                  <TableHead className="text-right">Received (foreign)</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">INR collected</TableHead>
                  <TableHead className="text-right">Eff. avg rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {m2Trips.map((t) => <Mode2Row key={t.tripId} t={t} />)}
              </TableBody>
            </Table>
          )}
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="fx-mode1-view">
          {m1Trips.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No INR quotes with a foreign supplier cost yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trip</TableHead>
                  <TableHead className="text-center">Foreign items</TableHead>
                  <TableHead className="text-center">Actual rates?</TableHead>
                  <TableHead className="text-right">INR impact (variance)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {m1Trips.map((t) => <Mode1Row key={t.tripId} t={t} />)}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
      <p className="text-xs text-slate-400">Variance is internal margin information only — a client's agreed INR price is never changed by it.</p>
    </div>
  );
}
